import { filter, first, isEmpty, map, values } from "lodash";
import { Alert } from "PFComponents/alert";
import { Typography } from "PFComponents/typography";
import { LocalisationKeys } from "PFTypes";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "../localisation_export.module.scss";

type ImportSummaryProps = {
  localisationKey: LocalisationKeys;
  processedRows?: { errors: Record<string, string[]>; rowNumber: number }[];
  fileErrors?: string[];
};

const getErrorMessage = (errors: Record<string, string[]>): string => first(first(values(errors))) || "";

export const ImportSummary = ({ localisationKey, processedRows, fileErrors }: ImportSummaryProps) => {
  const { t } = useTranslation("admin", { keyPrefix: "localisation" });

  const successfullyImported = useMemo(
    () => filter(processedRows, ({ errors }) => isEmpty(errors)).length,
    [processedRows]
  );

  const rowsErrors = useMemo(() => filter(processedRows, ({ errors }) => !isEmpty(errors)), [processedRows]);

  return (
    <section className={css.content}>
      <Typography variant="h4" noMargin>
        {t("importModal.summary")}
      </Typography>
      <Typography variant="bodyRegular" noMargin>
        {t(`tabs.${localisationKey}`)}
      </Typography>
      {!!successfullyImported && (
        <Alert
          className={css.summaryAlert}
          kind="success"
          title={
            <Typography variant="bodyBold" noMargin>
              {t("importModal.successfullyImported", { amount: successfullyImported })}
            </Typography>
          }
        />
      )}
      {!isEmpty(rowsErrors) && (
        <Alert
          className={css.summaryAlert}
          kind="error"
          title={t("importModal.errorsFound", { amount: rowsErrors.length })}
        >
          <span className={css.errorLines}>
            {map(rowsErrors, ({ rowNumber, errors }) => (
              <Typography variant="bodyRegular" noMargin>
                {t("importModal.lineError", { rowNumber, errorMessage: getErrorMessage(errors) })}
              </Typography>
            ))}
          </span>
        </Alert>
      )}
      {fileErrors && (
        <Alert
          className={css.summaryAlert}
          kind="error"
          title={t("importModal.errorsFound", { amount: fileErrors.length })}
        >
          <span className={css.errorLines}>
            {map(fileErrors, (error) => (
              <Typography variant="bodyRegular" noMargin>
                {error}
              </Typography>
            ))}
          </span>
        </Alert>
      )}
    </section>
  );
};
